import axios from '@/lib/axios'
import {
  buildAdminRequestConfig,
  type OrganizationId,
  type ScopedAdminRequestConfigOptions,
} from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  PublishSeasonalSchedulesInput,
  SeasonalContentPage,
  SeasonalContentResponse,
  SeasonalDesign,
  SeasonalDesignImpact,
  SeasonalDesignIndexFilters,
  SeasonalDesignInput,
  SeasonalDesignRecommendation,
  SeasonalDesignRecommendationInput,
  SeasonalDesignRevision,
  SeasonalScheduleImpact,
  SeasonalScheduleProfile,
  SeasonalScheduleProfilePatchInput,
  SeasonalStatus,
} from './seasonal-content.types'

export interface SeasonalOrganizationScope {
  organizationId: OrganizationId
}

/** Names one seasonal design; its Laravel routes require the id. */
export interface SeasonalDesignTarget {
  designId: number
}

/** Names one schedule profile; its Laravel routes require the id. */
export interface SeasonalScheduleProfileTarget {
  profileId: number
}

// Intersections rather than interfaces, so the mutation hooks can drop the organization scope
// from their variables.
export type SeasonalDesignScope = SeasonalOrganizationScope & SeasonalDesignTarget
export type SeasonalScheduleProfileScope = SeasonalOrganizationScope & SeasonalScheduleProfileTarget

/** The index query key's entries. */
export interface SeasonalDesignIndexKeyEntries extends SeasonalOrganizationScope {
  filters?: SeasonalDesignIndexFilters | undefined
}

/**
 * Entries of the query keys that name a seasonal design. The shared key builder can't mark an
 * entry required; the key builders always set it.
 */
export interface SeasonalDesignKeyEntries extends SeasonalOrganizationScope {
  designId?: number
}

export interface SeasonalDesignRevisionsKeyEntries extends SeasonalDesignKeyEntries {
  page?: number
  pageSize?: number
}

/** Query functions read only the key. */
type SeasonalQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

const config = (
  organizationId: OrganizationId,
  extra: Omit<ScopedAdminRequestConfigOptions, 'organizationId'> = {}
) => buildAdminRequestConfig({ organizationId, ...extra })

export const getSeasonalDesigns = ({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: Pick<QueryFunctionContext<[SeasonalDesignIndexKeyEntries]>, 'queryKey' | 'signal'>) =>
  axios.get<SeasonalContentPage<SeasonalDesign>>(
    '/seasonal-designs',
    config(organizationId, { params: { ...filters }, signal })
  )

export const getSeasonalDesign = ({
  queryKey: [{ organizationId, designId }],
}: SeasonalQueryContext<SeasonalDesignKeyEntries>) =>
  axios.get<SeasonalContentResponse<SeasonalDesign>>(
    `/seasonal-designs/${designId}`,
    config(organizationId)
  )

export const getSeasonalDesignImpact = ({ organizationId, designId }: SeasonalDesignScope) =>
  axios.get<SeasonalContentResponse<SeasonalDesignImpact>>(
    `/seasonal-designs/${designId}/impact`,
    config(organizationId)
  )

export const getSeasonalDesignRevisions = ({
  queryKey: [{ organizationId, designId, page = 1, pageSize = 10 }],
}: SeasonalQueryContext<SeasonalDesignRevisionsKeyEntries>) =>
  axios.get<SeasonalContentPage<SeasonalDesignRevision>>(
    `/seasonal-designs/${designId}/revisions`,
    config(organizationId, { params: { page, page_size: pageSize } })
  )

export const getSeasonalSchedules = ({
  queryKey: [{ organizationId }],
}: SeasonalQueryContext<SeasonalOrganizationScope>) =>
  axios.get<SeasonalContentResponse<SeasonalScheduleProfile[]>>(
    '/seasonal-schedule-profiles',
    config(organizationId)
  )

export const getSeasonalScheduleImpact = ({
  organizationId,
  profileId,
}: SeasonalScheduleProfileScope) =>
  axios.get<SeasonalContentResponse<SeasonalScheduleImpact>>(
    `/seasonal-schedule-profiles/${profileId}/impact`,
    config(organizationId)
  )

export const createSeasonalDesign = ({
  organizationId,
  values,
}: SeasonalOrganizationScope & { values: SeasonalDesignInput }) =>
  axios.post<SeasonalContentResponse<SeasonalDesign>>(
    '/seasonal-designs',
    values,
    config(organizationId)
  )

/** A changed seasonal tag on a published or used design saves a new design; follow its id. */
export const updateSeasonalDesign = ({
  organizationId,
  designId,
  values,
}: SeasonalDesignScope & { values: SeasonalDesignInput }) =>
  axios.put<SeasonalContentResponse<SeasonalDesign>>(
    `/seasonal-designs/${designId}`,
    values,
    config(organizationId)
  )

/** Saves the draft and publishes it; like an update, it can answer with a new design. */
export const publishSeasonalDesign = ({
  organizationId,
  designId,
  values,
}: SeasonalDesignScope & { values: SeasonalDesignInput }) =>
  axios.post<SeasonalContentResponse<SeasonalDesign>>(
    `/seasonal-designs/${designId}/publish`,
    values,
    config(organizationId)
  )

/** Archives the design, or restores it when it is archived. */
export const archiveSeasonalDesign = ({ organizationId, designId }: SeasonalDesignScope) =>
  axios.post<SeasonalContentResponse<SeasonalDesign>>(
    `/seasonal-designs/${designId}/archive`,
    {},
    config(organizationId)
  )

export const rollbackSeasonalDesign = ({
  organizationId,
  designId,
  revisionId,
}: SeasonalDesignScope & { revisionId: number }) =>
  axios.post<SeasonalContentResponse<SeasonalDesign>>(
    `/seasonal-designs/${designId}/revisions/${revisionId}/rollback`,
    {},
    config(organizationId)
  )

export const updateSeasonalSchedule = ({
  organizationId,
  profileId,
  values,
}: SeasonalScheduleProfileScope & { values: SeasonalScheduleProfilePatchInput }) =>
  axios.put<SeasonalContentResponse<SeasonalScheduleProfile>>(
    `/seasonal-schedule-profiles/${profileId}`,
    values,
    config(organizationId)
  )

export const publishSeasonalSchedule = ({
  organizationId,
  profileId,
  values,
}: SeasonalScheduleProfileScope & { values: SeasonalScheduleProfilePatchInput }) =>
  axios.post<SeasonalContentResponse<SeasonalScheduleProfile>>(
    `/seasonal-schedule-profiles/${profileId}/publish`,
    values,
    config(organizationId)
  )

/** Answers with every schedule profile. */
export const publishSeasonalSchedules = ({
  organizationId,
  values,
}: SeasonalOrganizationScope & { values: PublishSeasonalSchedulesInput }) =>
  axios.post<SeasonalContentResponse<SeasonalScheduleProfile[]>>(
    '/seasonal-schedule-profiles/publish',
    values,
    config(organizationId)
  )

export const getSeasonalDesignRecommendation = ({
  organizationId,
  seasonalTagId,
  excludeDesignIds = [],
}: SeasonalOrganizationScope & SeasonalDesignRecommendationInput) =>
  axios.get<SeasonalDesignRecommendation>(
    '/designs/seasonal-recommendation',
    config(organizationId, {
      params: {
        seasonal_tag_id: seasonalTagId,
        ...(excludeDesignIds.length ? { exclude_design_ids: excludeDesignIds } : {}),
      },
    })
  )

export const getSeasonalStatus = ({
  queryKey: [{ organizationId }],
}: SeasonalQueryContext<SeasonalOrganizationScope>) =>
  axios.get<SeasonalContentResponse<SeasonalStatus>>(
    '/designs/seasonal-status',
    config(organizationId)
  )
